import { useEffect, useId, useRef, useState } from 'react';
import {
  addJalaliMonths,
  compareIso,
  formatGregorianNumeric,
  formatJalaliNumeric,
  gregorianMonthLength,
  gregorianMonthName,
  gregorianWeekday,
  isoToJalali,
  jalaliToIso,
  jalaliWeekday,
  monthLength,
  monthName,
  todayIso,
  toFaDigits,
} from '../lib/jalali';

type CalendarMode = 'jalali' | 'gregorian';

const CAL_WEEKDAYS_FA = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'] as const;
const CAL_WEEKDAYS_EN = ['Sa', 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr'] as const;

type FlightDateFieldProps = {
  id: string;
  departure: string;
  returnDate?: string;
  /** Combined departure/return control. Single is one date, used for each multi-city leg. */
  variant: 'single' | 'range';
  roundTrip?: boolean;
  isFa: boolean;
  open: boolean;
  invalid?: boolean;
  error?: string;
  min?: string;
  onOpenChange: (open: boolean) => void;
  onDeparture: (iso: string) => void;
  onReturn?: (iso: string) => void;
  onAddReturn?: () => void;
  departureLabel: string;
  returnLabel: string;
  selectLabel: string;
  addReturnLabel: string;
  jalaliLabel: string;
  gregorianLabel: string;
  switchToGregorianLabel: string;
  switchToJalaliLabel: string;
  closeLabel: string;
  prevMonthLabel: string;
  nextMonthLabel: string;
};

function digits(value: string | number, isFa: boolean) {
  return isFa ? toFaDigits(value) : String(value);
}

export function FlightDateField({
  id,
  departure,
  returnDate = '',
  variant,
  roundTrip = false,
  isFa,
  open,
  invalid = false,
  error,
  min,
  onOpenChange,
  onDeparture,
  onReturn,
  onAddReturn,
  departureLabel,
  returnLabel,
  selectLabel,
  addReturnLabel,
  jalaliLabel,
  gregorianLabel,
  switchToGregorianLabel,
  switchToJalaliLabel,
  closeLabel,
  prevMonthLabel,
  nextMonthLabel,
}: FlightDateFieldProps) {
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const today = todayIso();
  const todayJ = isoToJalali(today)!;
  const [mode, setMode] = useState<CalendarMode>('jalali');
  const [viewJy, setViewJy] = useState(todayJ.jy);
  const [viewJm, setViewJm] = useState(todayJ.jm);
  const [viewGy, setViewGy] = useState(() => Number(today.slice(0, 4)));
  const [viewGm, setViewGm] = useState(() => Number(today.slice(5, 7)));
  const [pendingStart, setPendingStart] = useState(departure);
  const [pendingEnd, setPendingEnd] = useState(returnDate);
  const [hoverIso, setHoverIso] = useState('');

  const showReturn = variant === 'range' && roundTrip;
  const returnEnabled = showReturn;

  useEffect(() => {
    if (!open) return;
    setPendingStart(departure);
    setPendingEnd(roundTrip ? returnDate : '');
    setHoverIso('');
    const focus = isoToJalali(departure || today);
    if (focus) {
      setViewJy(focus.jy);
      setViewJm(focus.jm);
    }
    const iso = departure || today;
    setViewGy(Number(iso.slice(0, 4)));
    setViewGm(Number(iso.slice(5, 7)));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) onOpenChange(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onOpenChange(false);
      }
    }
    const timer = window.setTimeout(() => {
      document.addEventListener('click', onDocClick);
    }, 0);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onOpenChange]);

  const format = (iso: string) =>
    mode === 'jalali' ? formatJalaliNumeric(iso, isFa) : formatGregorianNumeric(iso, isFa);

  function shiftJalali(delta: number) {
    const next = addJalaliMonths(viewJy, viewJm, delta);
    setViewJy(next.jy);
    setViewJm(next.jm);
  }

  function shiftGregorian(delta: number) {
    const index = viewGy * 12 + (viewGm - 1) + delta;
    setViewGy(Math.floor(index / 12));
    setViewGm((index % 12) + 1);
  }

  function pick(iso: string) {
    if (min && compareIso(iso, min) < 0) return;
    if (!returnEnabled) {
      setPendingStart(iso);
      setPendingEnd('');
      return;
    }
    if (!pendingStart || pendingEnd) {
      setPendingStart(iso);
      setPendingEnd('');
      return;
    }
    if (compareIso(iso, pendingStart) < 0) {
      setPendingEnd(pendingStart);
      setPendingStart(iso);
      return;
    }
    setPendingEnd(iso);
  }

  function confirm() {
    if (!pendingStart) return;
    if (returnEnabled && !pendingEnd) return;
    onDeparture(pendingStart);
    onReturn?.(returnEnabled ? pendingEnd : '');
    onOpenChange(false);
  }

  const months = mode === 'jalali'
    ? [0, 1].map((offset) => {
        const { jy, jm } = addJalaliMonths(viewJy, viewJm, offset);
        return { key: `${jy}-${jm}`, title: monthName(jm, isFa), year: jy, cells: jalaliCells(jy, jm) };
      })
    : [0, 1].map((offset) => {
        const index = viewGy * 12 + (viewGm - 1) + offset;
        const year = Math.floor(index / 12);
        const month = (index % 12) + 1;
        return {
          key: `${year}-${month}`,
          title: gregorianMonthName(month, isFa),
          year,
          cells: gregorianCells(year, month),
        };
      });

  const weekdays = isFa ? CAL_WEEKDAYS_FA : CAL_WEEKDAYS_EN;
  const solarShort = isFa ? 'شمسی' : 'Solar';
  const gregorianShort = isFa ? 'میلادی' : 'Gregorian';
  const rangeEnd = returnEnabled && pendingStart ? pendingEnd || (hoverIso && hoverIso !== pendingStart ? hoverIso : '') : '';
  const rangeLo = rangeEnd && compareIso(rangeEnd, pendingStart) < 0 ? rangeEnd : pendingStart;
  const rangeHi = rangeEnd && rangeLo === pendingStart ? rangeEnd : pendingStart;
  const rangeCommitted = Boolean(returnEnabled && pendingStart && pendingEnd);

  return (
    <div
      className={`bw__date${open ? ' is-open' : ''}${invalid ? ' is-invalid' : ''}`}
      ref={rootRef}
    >
      <div className={`bw__box bw__box--date${open ? ' is-open' : ''}${invalid ? ' is-invalid' : ''}`}>
        <DateHalf
          id={id}
          label={departureLabel}
          value={departure ? format(departure) : ''}
          placeholder={departureLabel}
          active={open}
          onClick={() => onOpenChange(!open)}
        />
        {showReturn ? (
          <>
            <span className={`bw__date-split${returnEnabled ? '' : ' is-off'}`} aria-hidden="true" />
            <DateHalf
              id={`${id}-return`}
              label={returnLabel}
              value={returnEnabled && returnDate ? format(returnDate) : ''}
              placeholder={returnLabel}
              disabled={!returnEnabled}
              active={open && returnEnabled}
              onClick={() => {
                if (!returnEnabled) onAddReturn?.();
                onOpenChange(true);
              }}
            />
          </>
        ) : null}
      </div>
      {error ? <p className="bw__error">{error}</p> : null}

      {open ? (
        <div
          id={panelId}
          className="bw-cal"
          role="dialog"
          aria-modal="false"
          aria-label={departureLabel}
          dir={isFa ? 'rtl' : 'ltr'}
        >
          <div className="bw-cal__bar">
            <button type="button" className="bw-cal__close" onClick={() => onOpenChange(false)} aria-label={closeLabel}>
              <CloseIcon />
            </button>
            <p className="bw-cal__title">{departureLabel}</p>
            <div className="bw-cal__modes" role="group" aria-label={mode === 'jalali' ? jalaliLabel : gregorianLabel}>
              <button
                type="button"
                className={mode === 'jalali' ? 'is-on' : ''}
                aria-pressed={mode === 'jalali'}
                aria-label={mode === 'jalali' ? jalaliLabel : switchToJalaliLabel}
                onClick={() => setMode('jalali')}
              >
                {solarShort}
              </button>
              <button
                type="button"
                className={mode === 'gregorian' ? 'is-on' : ''}
                aria-pressed={mode === 'gregorian'}
                aria-label={mode === 'gregorian' ? gregorianLabel : switchToGregorianLabel}
                onClick={() => setMode('gregorian')}
              >
                {gregorianShort}
              </button>
            </div>
          </div>

          <div className="bw-cal__months" onMouseLeave={() => setHoverIso('')}>
            <button
              type="button"
              className="bw-cal__nav"
              aria-label={prevMonthLabel}
              onClick={() => (mode === 'jalali' ? shiftJalali(-1) : shiftGregorian(-1))}
            >
              <ChevronIcon direction="start" />
            </button>
            {months.map((month) => (
              <section key={month.key} className="bw-cal__month">
                <h3>
                  <span>{month.title}</span>
                  <small>{digits(month.year, isFa)}</small>
                </h3>
                <div className="bw-cal__week" aria-hidden="true">
                  {weekdays.map((day, index) => (
                    <span key={day} className={index === 6 ? 'is-weekend' : undefined}>
                      {day}
                    </span>
                  ))}
                </div>
                <div className="bw-cal__grid" role="grid">
                  {month.cells.map((cell, index) => {
                    if (!cell) return <span key={`${month.key}-e-${index}`} />;
                    const disabled = Boolean(min && compareIso(cell, min) < 0);
                    const selected = cell === pendingStart || (returnEnabled && cell === pendingEnd);
                    const spanned = Boolean(rangeEnd && rangeLo !== rangeHi);
                    const atStart = spanned && cell === rangeLo;
                    const atEnd = spanned && cell === rangeHi;
                    const inside =
                      spanned && compareIso(cell, rangeLo) > 0 && compareIso(cell, rangeHi) < 0;
                    const previewEnd = !rangeCommitted && hoverIso === cell && cell !== pendingStart;
                    return (
                      <button
                        key={cell}
                        type="button"
                        role="gridcell"
                        className={`bw-cal__day${selected ? ' is-selected' : ''}${
                          atStart ? ' is-start' : ''
                        }${atEnd ? ' is-end' : ''}${inside && rangeCommitted ? ' is-ranged' : ''}${
                          inside && !rangeCommitted ? ' is-preview' : ''
                        }${previewEnd ? ' is-preview-end' : ''}${cell === today ? ' is-today' : ''}${
                          index % 7 === 6 ? ' is-weekend' : ''
                        }`}
                        disabled={disabled}
                        aria-selected={selected}
                        aria-current={cell === today ? 'date' : undefined}
                        onMouseEnter={() => {
                          if (!disabled) setHoverIso(cell);
                        }}
                        onClick={() => pick(cell)}
                      >
                        <span>{digits(dayOf(cell, mode), isFa)}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
            <button
              type="button"
              className="bw-cal__nav bw-cal__nav--next"
              aria-label={nextMonthLabel}
              onClick={() => (mode === 'jalali' ? shiftJalali(1) : shiftGregorian(1))}
            >
              <ChevronIcon direction="end" />
            </button>
          </div>

          <div className="bw-cal__foot">
            <div className="bw-cal__chosen">
              <div className={`bw-cal__chip${pendingStart ? '' : ' is-empty'}`}>
                <span>{departureLabel}</span>
                <strong>{pendingStart ? format(pendingStart) : '—'}</strong>
              </div>
              {returnEnabled ? (
                <div className={`bw-cal__chip${pendingEnd ? '' : ' is-empty'}`}>
                  <span>{returnLabel}</span>
                  <strong>{pendingEnd ? format(pendingEnd) : '—'}</strong>
                </div>
              ) : null}
            </div>
            <div className="bw-cal__actions">
              {variant === 'range' && !roundTrip && onAddReturn ? (
                <button type="button" className="bw-cal__link" onClick={onAddReturn}>
                  {addReturnLabel}
                </button>
              ) : null}
              <button
                type="button"
                className="bw-cal__ok"
                disabled={!pendingStart || (returnEnabled && !pendingEnd)}
                onClick={confirm}
              >
                {selectLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function dayOf(iso: string, mode: CalendarMode) {
  if (mode === 'gregorian') return Number(iso.slice(8));
  return isoToJalali(iso)?.jd ?? Number(iso.slice(8));
}

function jalaliCells(jy: number, jm: number): Array<string | null> {
  const length = monthLength(jy, jm);
  const offset = jalaliWeekday(jy, jm, 1);
  const items: Array<string | null> = Array.from({ length: offset }, () => null);
  for (let day = 1; day <= length; day += 1) items.push(jalaliToIso(jy, jm, day));
  while (items.length % 7 !== 0) items.push(null);
  return items;
}

function gregorianCells(year: number, month: number): Array<string | null> {
  const length = gregorianMonthLength(year, month);
  const offset = gregorianWeekday(year, month, 1);
  const items: Array<string | null> = Array.from({ length: offset }, () => null);
  for (let day = 1; day <= length; day += 1) {
    items.push(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
  }
  while (items.length % 7 !== 0) items.push(null);
  return items;
}

function DateHalf({
  id,
  label,
  value,
  placeholder,
  disabled = false,
  active,
  onClick,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  disabled?: boolean;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      id={id}
      className={`bw__date-half${disabled ? ' is-disabled' : ''}${value ? ' has-value' : ''}`}
      aria-haspopup="dialog"
      aria-expanded={active}
      disabled={disabled}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      <CalendarIcon />
      <span className="bw__date-text">{value || placeholder}</span>
      {value ? <span className="bw__float">{label}</span> : null}
    </button>
  );
}

function CalendarIcon() {
  return (
    <svg className="bw__glyph" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M7 2.75a.75.75 0 0 1 1.5 0V4h7V2.75a.75.75 0 0 1 1.5 0V4h1.25A2.75 2.75 0 0 1 21 6.75v11.5A2.75 2.75 0 0 1 18.25 21H5.75A2.75 2.75 0 0 1 3 18.25V6.75A2.75 2.75 0 0 1 5.75 4H7V2.75zM4.5 9.5v8.75c0 .69.56 1.25 1.25 1.25h12.5c.69 0 1.25-.56 1.25-1.25V9.5h-15z"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M5.2 4.1 10 8.9l4.8-4.8 1.1 1.1L11.1 10l4.8 4.8-1.1 1.1L10 11.1l-4.8 4.8-1.1-1.1L8.9 10 4.1 5.2z"
      />
    </svg>
  );
}

function ChevronIcon({ direction }: { direction: 'start' | 'end' }) {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d={
          direction === 'start'
            ? 'M12.2 4.3a.9.9 0 0 1 0 1.27L8.78 9l3.42 3.43a.9.9 0 1 1-1.27 1.27l-4.06-4.06a.9.9 0 0 1 0-1.28l4.06-4.06a.9.9 0 0 1 1.27 0z'
            : 'M7.8 4.3a.9.9 0 0 1 1.27 0l4.06 4.06a.9.9 0 0 1 0 1.28l-4.06 4.06a.9.9 0 1 1-1.27-1.27L11.22 9 7.8 5.57a.9.9 0 0 1 0-1.27z'
        }
      />
    </svg>
  );
}
